import { useEffect, useRef } from 'react'

/**
 * Editor de texto enriquecido básico (negrita, cursiva, subrayado y listas).
 *
 * Es un envoltorio ligero sobre un `contenteditable` que produce HTML simple.
 * El pegado se fuerza a texto plano para evitar inyectar HTML externo. El valor
 * se sincroniza hacia el DOM solo cuando difiere (p. ej. al reiniciar el form),
 * para no mover el cursor en cada pulsación.
 *
 * @component
 * @param {Object} props
 * @param {string} props.value - Contenido HTML actual.
 * @param {(html: string) => void} props.onChange - Notifica el HTML nuevo.
 * @param {string} [props.placeholder] - Texto guía cuando está vacío.
 * @param {string} [props.className] - Clases extra para el área editable.
 * @param {string} [props.ariaLabel] - Etiqueta accesible.
 * @returns {JSX.Element}
 */
const RichTextEditor = ({
  value = '',
  onChange,
  placeholder = 'Escribe aquí…',
  className = '',
  ariaLabel = 'Editor de texto enriquecido',
}) => {
  const ref = useRef(null)

  // Sincroniza value externo → DOM solo cuando difiere (reset, carga inicial)
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== value) {
      ref.current.innerHTML = value || ''
    }
  }, [value])

  const emitChange = () => onChange?.(ref.current?.innerHTML || '')

  /**
   * Ejecuta un comando de formato sobre la selección actual.
   * @param {string} command - Comando de document.execCommand.
   */
  const exec = (command) => {
    ref.current?.focus()
    document.execCommand(command, false, null)
    emitChange()
  }

  const handlePaste = (e) => {
    e.preventDefault()
    const text = e.clipboardData.getData('text/plain')
    document.execCommand('insertText', false, text)
  }

  const tools = [
    { cmd: 'bold', icon: 'fa-bold', label: 'Negrita' },
    { cmd: 'italic', icon: 'fa-italic', label: 'Cursiva' },
    { cmd: 'underline', icon: 'fa-underline', label: 'Subrayado' },
    {
      cmd: 'insertUnorderedList',
      icon: 'fa-list-ul',
      label: 'Lista con viñetas',
    },
    { cmd: 'insertOrderedList', icon: 'fa-list-ol', label: 'Lista numerada' },
  ]

  return (
    <div className="rte-wrapper">
      <div className="rte-toolbar">
        {tools.map((t, i) => (
          <span key={t.cmd} className="d-inline-flex align-items-center">
            {i === 3 && <span className="rte-divider" />}
            <button
              type="button"
              className="rte-tool-btn"
              title={t.label}
              aria-label={t.label}
              // onMouseDown para no perder la selección del editor
              onMouseDown={(e) => {
                e.preventDefault()
                exec(t.cmd)
              }}
            >
              <i className={`fa-solid ${t.icon}`}></i>
            </button>
          </span>
        ))}
      </div>

      <div
        ref={ref}
        className={`rte-content form-control-slate ${className}`}
        contentEditable
        role="textbox"
        aria-multiline="true"
        aria-label={ariaLabel}
        data-placeholder={placeholder}
        onInput={emitChange}
        onBlur={emitChange}
        onPaste={handlePaste}
        suppressContentEditableWarning
      />

      <style>{`
        .rte-toolbar {
          display: flex;
          align-items: center;
          gap: 4px;
          padding: 6px;
          background: var(--bg-surface-2);
          border: 1px solid var(--border);
          border-bottom: none;
          border-radius: 10px 10px 0 0;
        }
        .rte-divider {
          width: 1px;
          height: 18px;
          background: var(--border);
          margin: 0 4px;
        }
        .rte-tool-btn {
          width: 30px;
          height: 30px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border: 1px solid transparent;
          background: transparent;
          color: var(--text-2);
          border-radius: 7px;
          font-size: 0.85rem;
          transition: all 0.15s;
        }
        .rte-tool-btn:hover {
          background: var(--bg-surface);
          color: var(--brand);
          border-color: var(--border);
        }
        .rte-content {
          min-height: 140px;
          max-height: 360px;
          overflow-y: auto;
          border-top-left-radius: 0 !important;
          border-top-right-radius: 0 !important;
          line-height: 1.6;
          cursor: text;
        }
        .rte-content:empty::before {
          content: attr(data-placeholder);
          color: var(--text-muted);
          pointer-events: none;
        }
        .rte-content ul,
        .rte-content ol {
          margin: 0 0 0 1.1rem;
          padding-left: 0.4rem;
        }
        .rte-content p { margin: 0 0 0.4rem; }
      `}</style>
    </div>
  )
}

export default RichTextEditor
